import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { USER_STATUS } from 'constants/user'
import { BLOCKED_ACCOUNT_MSG } from 'constants/wording'
import { useCardIframeUrl, useDataToggleStore } from 'store'

// utils
import { differenceInHoursAndMinutes } from 'utils'
import { trackEvent } from 'analytics/trackFunctions'
import handleNotification from 'utils/toast/handleNotification'
import { checkTokenDate } from '../utils/token'
import storage from '../utils/storage'

// api
import { addResponseInterceptor, ejectResponseInterceptor } from '../api/base'
import { signin, signinPhone, refreshToken } from '../api/auth'

// constants
import { storageKey } from '../constants/storage'
import { signInTypes } from '../constants/signin'

const { DISABLED, BLOCKED, REJECTED_AUDIT } = USER_STATUS

export const UserContext = React.createContext()

export const UserProvider = (props) => {
  const [user, setUser] = useState(() => storage.get(storageKey.USER))
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const { updatePhysicalCardUrl, updateVirtualCardUrl } = useCardIframeUrl()
  const { updateIsHidden } = useDataToggleStore()

  const { errorToast, successToast } = handleNotification()
  const isExpired = checkTokenDate()

  // To intercept when jwt has expired
  useEffect(() => {
    const interceptor = addResponseInterceptor(
      (res) => {
        if (res.status === 401 && isExpired) {
          refreshUserToken()
        }

        if (res.status === 401 || res.status === 404) {
          const message = res.data?.message
          setError(message)

          if (message) {
            errorToast(message)
          }
        }

        if (res.status === 403) {
          const { status, next_attempt } = res.data?.errors || {}

          if (status === DISABLED) {
            errorToast(
              `Tu cuenta se encuentra deshabilitada por varios intentos de inicio de sesión fallidos. Podés volver a intentar en ${differenceInHoursAndMinutes(
                new Date(),
                new Date(next_attempt)
              )}.`
            )
          }

          if (status === BLOCKED) {
            errorToast(BLOCKED_ACCOUNT_MSG)
          }

          if (status === REJECTED_AUDIT) {
            navigate('/cuenta-rechazada')
          }
        }
        return res
      },
      (err) => err
    )
    return () => {
      ejectResponseInterceptor(interceptor)
    }
  }, [])

  // If an error occurs then we clear the user
  useEffect(() => {
    if (error) setUser(null)
  }, [error])

  // If the user is updated then we update the sessionStorage
  useEffect(() => {
    storage.set(storageKey.USER, user || null)
  }, [user])

  function login(credentials, recaptchaToken) {
    setError(null)
    const signInMethod =
      credentials.field_type === signInTypes.EMAIL ||
      credentials.field_type === signInTypes.IDENTITY_NUMBER
        ? signin
        : signinPhone

    const params = {
      ...credentials,
      recaptcha: recaptchaToken
    }

    return signInMethod(params).then((res) => {
      if (
        (credentials.field_type === 'email' ||
          credentials.field_type === 'identity_number') &&
        res.success
      ) {
        setUser(res.data)
        if (!res.data) {
          trackEvent({
            eventName: 'bille_login_ingresar_error',
            props: {
              id: credentials.identification,
              error: JSON.stringify(res)
            }
          })
          setError('Usuario y/o contraseña inválida/incorrecta')
        }
        storage.set(storageKey.USER, res.data || null)
      } else if (credentials.field_type === 'phone_number') {
        if (!res.success) {
          if (res.error?.errors?.blocked) {
            errorToast(BLOCKED_ACCOUNT_MSG)
            return
          }
          trackEvent({
            eventName: 'bille_login_ingresar_error',
            props: {
              id: credentials.identification,
              error: JSON.stringify(res)
            }
          })
          errorToast(
            'No pudimos comprobar tu número de teléfono. Recuerda incluir el código de área pero sin el 15. No incluyas el 0 previo al código de área.'
          )
          return
        }
        successToast('Te enviamos un link por SMS a tu celular')
      }
    })
  }

  const logout = () => {
    setUser(null)
    storage.del('user')
    storage.del('membership')
    storage.del('HOME_CARD_URL')
    updatePhysicalCardUrl(null)
    updateVirtualCardUrl(null)
    updateIsHidden({ state: true, type: 'PHYSICAL' })
    updateIsHidden({ state: true, type: 'VIRTUAL' })
    navigate('/')
  }

  const refreshUserToken = () =>
    refreshToken({
      refresh_token: user.refresh_token
    }).then((data) => {
      storage.set(storageKey.USER, data.data || null)
      setUser(data.data)
    })

  const options = useMemo(
    () => ({
      isUserLoggedIn: user !== null,
      user,
      error,
      login,
      logout,
      setUser,
      refreshUserToken
    }),
    [user, error]
  )

  return (
    <UserContext.Provider
      value={options}
      {...props}
      displayName='UserContext'
    />
  )
}
